<template>
  <div class="floors-page">
    <b-card no-body class="main-card">
      <b-card-body class="p-3">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 class="mb-1">{{ $t("reception.floors") }}</h2>
            <p class="text-muted mb-0">
              {{ $t("reception.select-floor-to-view-rooms") }}
            </p>
          </div>
          <b-button
            variant="outline-primary"
            size="sm"
            @click="$router.push({ name: 'Reception-Departments' })"
          >
            <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
            {{ $t("common.back") }}
          </b-button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
          <b-spinner :label="$t('common.loading')" />
        </div>

        <!-- Floors Grid -->
        <div v-else-if="floorsData.length > 0">
          <b-row>
            <b-col
              v-for="floor in floorsData"
              :key="floor.id"
              cols="12"
              sm="6"
              md="4"
              lg="3"
              class="mb-3"
            >
              <b-card
                no-body
                class="floor-card h-100"
                @click="goToFloorRooms(floor)"
              >
                <div class="floor-card-content">
                  <div class="floor-icon-wrapper">
                    <feather-icon
                      icon="LayersIcon"
                      size="32"
                      class="floor-icon"
                    />
                  </div>
                  <div class="floor-info">
                    <h5 class="floor-name mb-2">{{ floor.name }}</h5>
                    <div class="floor-stats" v-if="floor.rooms_count !== undefined">
                      <div class="stat-item">
                        <feather-icon
                          icon="HomeIcon"
                          size="14"
                          class="stat-icon"
                        />
                        <span class="stat-text">
                          {{ $t("reception.rooms") }}: {{ floor.rooms_count }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="floor-arrow">
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="20"
                      class="arrow-icon"
                    />
                  </div>
                </div>
              </b-card>
            </b-col>
          </b-row>

          <!-- Pagination -->
          <div v-if="totalFloors > 0" class="mt-4">
            <b-row>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-start"
              >
                <span class="text-muted">
                  {{ $t("common.showing") }} {{ (page - 1) * take + 1 }}
                  {{ $t("common.to") }}
                  {{ Math.min(page * take, totalFloors) }}
                  {{ $t("common.of") }} {{ totalFloors }}
                  {{ $t("reception.floors") }}
                </span>
              </b-col>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-end"
              >
                <b-pagination
                  v-model="page"
                  :total-rows="totalFloors"
                  :per-page="take"
                  first-number
                  last-number
                  class="mb-0 mt-1 mt-sm-0"
                  prev-class="prev-item"
                  next-class="next-item"
                >
                  <template #prev-text>
                    <feather-icon icon="ChevronLeftIcon" size="18" />
                  </template>
                  <template #next-text>
                    <feather-icon icon="ChevronRightIcon" size="18" />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </div>

        <!-- Empty State -->
        <b-card v-else no-body class="empty-state-card">
          <div class="text-center py-5">
            <feather-icon
              icon="LayersIcon"
              size="48"
              class="text-muted mb-2"
            />
            <p class="text-muted">{{ $t("common.no_results") }}</p>
          </div>
        </b-card>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BPagination,
} from "bootstrap-vue";

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BPagination,
  },
  data() {
    return {
      loading: true,
      floorsData: [],
      totalFloors: 0,
      page: 1,
      take: 100,
    };
  },
  watch: {
    page(newPage, oldPage) {
      if (oldPage !== undefined && newPage !== oldPage && !this.loading) {
        this.getFloors();
      }
    },
  },
  mounted() {
    this.getFloors();
  },
  methods: {
    async getFloors() {
      this.loading = true;
      try {
        const url = `/floor?page=${this.page}&take=${this.take}&sort=-id`;
        await this.getList(url);
        this.totalFloors = this.all_data.total || 0;
        this.floorsData = this.all_data.data || [];
      } catch (error) {
        console.error("Error fetching floors:", error);
        this.floorsData = [];
        this.totalFloors = 0;
      } finally {
        this.loading = false;
      }
    },
    goToFloorRooms(floor) {
      this.$router.push({
        name: "Reception-Floor-Rooms",
        params: { floorId: floor.id },
        query: { floorName: floor.name },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.floors-page {
  padding: 1rem;
}

.main-card {
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.floor-card {
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  cursor: pointer;
  overflow: hidden;
}

.floor-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-color: #667eea;
}

.floor-card-content {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
}

.floor-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  flex-shrink: 0;
}

.floor-icon {
  color: #ffffff;
}

.floor-info {
  flex: 1;
  width: 100%;
}

.floor-name {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.4;
}

.floor-stats {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.stat-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background-color: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.stat-icon {
  color: #6b7280;
  flex-shrink: 0;
}

.stat-text {
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
}

.floor-arrow {
  position: absolute;
  top: 1rem;
  right: 1rem;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.floor-card:hover .floor-arrow {
  opacity: 1;
}

.arrow-icon {
  color: #667eea;
}

.empty-state-card {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
}

/* Responsive Design */
@media (max-width: 768px) {
  .floor-card-content {
    padding: 1.25rem;
  }

  .floor-icon-wrapper {
    width: 56px;
    height: 56px;
    margin-bottom: 0.875rem;
  }

  .floor-name {
    font-size: 1rem;
  }
}
</style>

